// dependencyEngine.js
// V5 — KHÔNG còn round. Chỉ 1 danh sách câu hỏi duy nhất:
//   - answered: đã trả lời (theo answerHistory)
//   - pending: chưa trả lời, liên tục re-evaluate (sắp xếp + ẩn hiện)
//
// Đầu vào (BE gửi xuống):
//   - questions: [{id, question:{vi,en}, hint?, enables:[code], valueMapping?}]
//   - businessFeatures: [{feature, when}]   ← BE đã lọc về tập liên quan
//   - currentSettings: {code: value}        ← snapshot DB
//
// Cách derive prereqs cho 1 question Q (enables=[X1,X2...]):
//   - Với mỗi Xi, walk businessFeatures[Xi].when (đệ quy qua nested groups)
//     → thu thập setting code có whenSys[code] === 1
//     → bỏ qua các giá trị không phải BusinessSetting như locale/currency.
//   - Tập kết quả = Q._requires.

// ─── walker đệ quy ─────────────────────────────────────────────
// Trả về list "groups" theo cấu trúc: [{type:'AND'|'OR', codes:[...]}].
// Mỗi nested level (gr1, gr2, ...) trong feature.when sinh ra 1 group,
// type được xác định bởi whenSysType ('OR' → OR, mặc định AND).
// Cùng level nhiều group → AND giữa các group (every group must pass).
function _walkGroups(when, parentType) {
    if (!when || typeof when !== 'object') return [];
    var out = [];
    if (when.whenSys && typeof when.whenSys === 'object') {
        var codes = [];
        var valueChecks = [];
        Object.keys(when.whenSys).forEach(function (k) {
            if (k === 'Sys_BaseLanguage') return;
            var vv = when.whenSys[k];
            if (vv === 1) {
                codes.push(k);
                return;
            }
            // Bất kỳ value khác 1 (string / array / 0 / number khác) → VALUE check.
            // Bao gồm: Sys_BaseCurrency, business_model, ...
            var arr = Array.isArray(vv) ? vv.slice() : [vv];
            valueChecks.push({ code: k, values: arr });
        });
        if (codes.length > 0) {
            var type = (when.whenSysType === 'OR') ? 'OR' : (parentType || 'AND');
            out.push({ type: type, codes: codes });
        }
        valueChecks.forEach(function (vc) {
            out.push({ type: 'VALUE', code: vc.code, values: vc.values });
        });
    }
    Object.keys(when).forEach(function (k) {
        if (k === 'whenSys' || k === 'whenSysType' || k === 'whenUser' || k === 'whenSession') return;
        if (typeof when[k] === 'object' && when[k] !== null) {
            // Nested level (gr1, gr2): type lấy từ con (whenSysType bên trong) hoặc kế thừa.
            var subType = when[k].whenSysType === 'OR' ? 'OR' : 'AND';
            _walkGroups(when[k], subType).forEach(function (g) { out.push(g); });
        }
    });
    return out;
}

// Backward-compatible: trả về flat array codes (dùng cho topo sort).
export function extractPrerequisites(featureCode, businessFeatures) {
    var def = (businessFeatures || []).find(function (f) { return f.feature === featureCode; });
    if (!def || !def.when) return [];
    var seen = {};
    _walkGroups(def.when, 'AND').forEach(function (g) {
        if (g.codes) g.codes.forEach(function (c) { seen[c] = true; });
    });
    return Object.keys(seen);
}

// Trả về groups (giữ semantic AND/OR).
function _extractPrereqGroups(featureCode, businessFeatures) {
    var def = (businessFeatures || []).find(function (f) { return f.feature === featureCode; });
    if (!def || !def.when) return [];
    return _walkGroups(def.when, 'AND');
}

function _deriveRequires(question, businessFeatures) {
    // Flat list (cho topo sort)
    var seen = {};
    // Groups (cho visibility check)
    var groups = [];
    (question.enables || []).forEach(function (code) {
        var gs = _extractPrereqGroups(code, businessFeatures);
        gs.forEach(function (g) {
            groups.push(g);
            // VALUE groups (Sys_BaseCurrency) không tham gia topo sort vì không phải feature flag.
            if (g.codes) g.codes.forEach(function (c) { seen[c] = true; });
        });
    });
    (question.requires || []).forEach(function (code) {
        seen[code] = true;
        groups.push({ type: 'AND', codes: [code] });
    });
    return { flat: Object.keys(seen), groups: groups };
}

// ─── topo sort: lazy promote ────────────────────────────────────
// Preserve thứ tự gốc ở mức tối đa; chỉ DỜI một câu khi enabler của nó nằm SAU
// trong thứ tự gốc. Nếu enabler đã đứng trước → giữ nguyên vị trí gốc.
// Mục tiêu: giữ topical grouping (use_Module_Promotion + q_promo_* nằm liền kề
// trong nhóm "pricing"), thay vì gom tất cả "level 0" lên đầu và "level 1" xuống sau.
function _topoSort(enriched) {
    var enablerByCode = {};
    enriched.forEach(function (q, idx) {
        (q.enables || []).forEach(function (code) {
            if (enablerByCode[code] === undefined) enablerByCode[code] = idx;
        });
    });

    // outputPos[i] = vị trí "sớm nhất" mà câu i được phép nằm.
    // Tính tuần tự theo original index (Kahn-style với input đã gần đúng thứ tự).
    var outputPos = new Array(enriched.length);
    for (var i = 0; i < enriched.length; i++) {
        var pos = i;        // mặc định = vị trí gốc
        var requires = enriched[i]._requiresFlat || [];
        for (var j = 0; j < requires.length; j++) {
            var enablerIdx = enablerByCode[requires[j]];
            if (enablerIdx === undefined) continue;        // require không có enabler → bỏ qua
            if (enablerIdx === i) continue;                 // tự enable (hiếm)
            // outputPos[enablerIdx] đã tính (vì enabler có index nhỏ hơn) HOẶC
            // chưa tính (enabler đứng SAU trong original) → cần dời câu này xuống.
            var enablerPos = (outputPos[enablerIdx] !== undefined)
                ? outputPos[enablerIdx]
                : enablerIdx;                               // tạm dùng original index
            if (enablerPos + 1 > pos) pos = enablerPos + 1;
        }
        outputPos[i] = pos;
    }

    // Sort: outputPos asc, original index asc (stable tie-break).
    var indexed = enriched.map(function (q, idx) { return { q: q, idx: idx, p: outputPos[idx] }; });
    indexed.sort(function (a, b) {
        if (a.p !== b.p) return a.p - b.p;
        return a.idx - b.idx;
    });
    return indexed.map(function (x) { return x.q; });
}

// ─── public API ─────────────────────────────────────────────────

// Build sorted master list 1 lần lúc start. Trả về [{...q, _requiresFlat, _requireGroups}].
export function buildQuestionList(questions, businessFeatures) {
    var enriched = (questions || []).map(function (q) {
        var d = _deriveRequires(q, businessFeatures);
        return Object.assign({}, q, { _requiresFlat: d.flat, _requireGroups: d.groups });
    });
    return _topoSort(enriched);
}

// Q hiển thị được khi mọi require group đều thoả:
//   - AND group: ALL codes phải =1 trong settingsState
//   - OR  group: ÍT NHẤT 1 code phải =1
// Không có group → luôn visible.
function _isVisible(q, settingsState) {
    var groups = q._requireGroups || [];
    if (groups.length === 0) return true;
    return groups.every(function (g) {
        if (!g) return true;
        if (g.type === 'VALUE') {
            var v = settingsState[g.code];
            return g.values && g.values.indexOf(v) >= 0;
        }
        if (!g.codes || g.codes.length === 0) return true;
        if (g.type === 'OR') {
            return g.codes.some(function (c) { return settingsState[c] === 1; });
        }
        return g.codes.every(function (c) { return settingsState[c] === 1; });
    });
}

export function isQuestionVisible(question, settingsState) {
    return _isVisible(question, settingsState || {});
}

// "Đã trả lời" = answers[qId] không phải undefined/null.
// Bao gồm: 0, 1, hoặc string (cho multi-choice).
// Riêng multi_pick + custom_panel: answer được lưu trong customPanelData[qId]
// (vd: {codes:[...]} hoặc {rows:[...]}).
function _isAnswered(q, answers, panel) {
    if (q && (q.type === 'multi_pick' || q.type === 'custom_panel')) {
        return panel && panel[q.id] !== undefined;
    }
    var a = answers[q.id];
    return a !== undefined && a !== null;
}

// Câu kế tiếp: đầu tiên trong sorted list, chưa trả lời, visible.
// Trả về null nếu hết.
export function getNextQuestion(sortedQuestions, answers, settingsState, customPanelData) {
    customPanelData = customPanelData || {};
    for (var i = 0; i < sortedQuestions.length; i++) {
        var q = sortedQuestions[i];
        if (_isAnswered(q, answers, customPanelData)) continue;
        if (!_isVisible(q, settingsState)) continue;
        return q;
    }
    return null;
}

// Đếm số câu pending visible (cho progress bar).
export function getVisiblePendingCount(sortedQuestions, answers, settingsState, customPanelData) {
    customPanelData = customPanelData || {};
    var n = 0;
    for (var i = 0; i < sortedQuestions.length; i++) {
        var q = sortedQuestions[i];
        if (_isAnswered(q, answers, customPanelData)) continue;
        if (!_isVisible(q, settingsState)) continue;
        n++;
    }
    return n;
}

// ─── apply / revert ─────────────────────────────────────────────

function _resolveValue(question, code, answer) {
    var mapping = question.valueMapping && question.valueMapping[code];
    if (mapping) {
        // String key (multi-choice): mapping[answer] → value.
        if (typeof answer === 'string' && mapping[answer] !== undefined) return mapping[answer];
        if (answer === 1) return mapping.yes;
        if (answer === 0) return mapping.no;
        return undefined;
    }
    if (answer === 1) return 1;
    if (answer === 0) return 0;
    if (typeof answer === 'string' && answer.length > 0) return answer;
    return undefined;
}

export function applyAnswer(settingsState, question, answer) {
    var next = Object.assign({}, settingsState);
    // enables: toggle 0/1 (Yes→1, No→0). Dùng cho feature CHÍNH của câu hỏi.
    (question.enables || []).forEach(function (code) {
        var v = _resolveValue(question, code, answer);
        if (v !== undefined) next[code] = v;
    });
    // ensures: CHỈ set =1 khi Yes, KHÔNG set 0 khi No. Dùng cho prereq upstream
    // (vd: cafe → use_Item_Food là prereq của ngành, không nên bị tắt bởi câu workflow).
    if (answer === 1 || (typeof answer === 'string' && answer.length > 0)) {
        (question.ensures || []).forEach(function (code) {
            next[code] = 1;
        });
    }
    return next;
}

// Rebuild settingsState = currentSettings + apply answers theo answerHistory order.
// Bỏ qua answer cho Q đang ở trạng thái invisible (prereq chưa thoả).
export function rebuildSettingsState(currentSettings, sortedQuestions, answers, answerHistory, customPanelData) {
    var r = rebuildHistoryAndState(currentSettings, sortedQuestions, answers, answerHistory, customPanelData);
    return r.settingsState;
}

// Walk answerHistory theo order, GIỮ những câu vẫn còn visible (prereq thoả ở thời điểm tới câu đó),
// LOẠI những câu đã trở thành invisible do user sửa câu trước.
// Trả về: { answers, history, settingsState, customPanelData } đã đồng bộ.
export function rebuildHistoryAndState(currentSettings, sortedQuestions, answers, answerHistory, customPanelData) {
    var state = Object.assign({}, currentSettings || {});
    var byId = {};
    sortedQuestions.forEach(function (q) { byId[q.id] = q; });
    var keptHistory = [];
    var keptAnswers = {};
    var keptPanels = Object.assign({}, customPanelData || {});
    (answerHistory || []).forEach(function (qId) {
        var ans = answers[qId];
        var q = byId[qId];
        if (!q) return;
        if (q.type === 'multi_pick') {
            if (keptPanels[qId] === undefined) return;
        } else {
            if (ans === undefined || ans === null) return;
        }
        // Pre-apply ensures TRƯỚC khi check visibility — câu đã trả lời yes-ish thì prereq
        // upstream (vd: use_Item_Food cho cafe) phải được coi như đã thoả tại thời điểm này.
        // Nếu để check visibility với state thô từ DB, câu sẽ bị drop oan vì prereq chưa =1.
        var isYesish = (ans === 1) || (typeof ans === 'string' && ans.length > 0) || (q.type === 'multi_pick');
        if (isYesish) {
            (q.ensures || []).forEach(function (code) { state[code] = 1; });
        }
        if (!_isVisible(q, state)) {
            // Rollback ensures vừa apply (chưa đủ điều kiện thực sự)
            if (isYesish) {
                (q.ensures || []).forEach(function (code) {
                    if (currentSettings && currentSettings[code] !== undefined) state[code] = currentSettings[code];
                    else delete state[code];
                });
            }
            delete keptPanels[qId];
            return;       // câu đã trở thành invisible → drop
        }
        keptHistory.push(qId);
        if (ans !== undefined && ans !== null) keptAnswers[qId] = ans;
        (q.enables || []).forEach(function (code) {
            var v = _resolveValue(q, code, ans);
            if (v !== undefined) state[code] = v;
        });
    });
    return { history: keptHistory, answers: keptAnswers, settingsState: state, customPanelData: keptPanels };
}

export default {
    buildQuestionList,
    getNextQuestion,
    getVisiblePendingCount,
    applyAnswer,
    rebuildSettingsState,
    rebuildHistoryAndState,
    isQuestionVisible,
    extractPrerequisites
};
